"use client";

import { useState, useCallback } from "react";

// ── Types ──────────────────────────────────────────────────────

interface GenerationParamsProps {
  bpm: number;
  measures: number;
  onBpmChange: (v: number) => void;
  onMeasuresChange: (v: number) => void;
}

// ── Constants ──────────────────────────────────────────────────

const BPM_MIN = 40;
const BPM_MAX = 200;
const MEASURES_MIN = 1;
const MEASURES_MAX = 32;

// ── Stepper button ─────────────────────────────────────────────

function StepperButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 transition-all duration-150 cursor-pointer select-none hover:border-[rgba(139,92,246,0.25)] hover:bg-[rgba(139,92,246,0.08)] active:bg-[rgba(139,92,246,0.15)]"
      style={{
        backgroundColor: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
      title={label}
    >
      {children}
    </button>
  );
}

// ── Stepper row ────────────────────────────────────────────────

function Stepper({
  label,
  value,
  min,
  max,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  suffix?: string;
  onChange: (v: number) => void;
}) {
  const clamp = (v: number) => Math.max(min, Math.min(max, v));

  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-medium text-text-muted min-w-[60px]">
        {label}
      </span>
      <StepperButton onClick={() => onChange(clamp(value - 1))} label={`-1`}>
        <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeLinecap="round">
          <path d="M3 7h8" />
        </svg>
      </StepperButton>

      <div
        className="h-7 px-3 rounded-md flex items-center justify-center gap-1"
        style={{
          backgroundColor: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
          minWidth: suffix ? "80px" : "48px",
        }}
      >
        <span className="text-[12px] font-semibold text-text-primary tabular-nums">
          {value}
        </span>
        {suffix && (
          <span className="text-[9px] font-medium text-text-muted uppercase tracking-wider">
            {suffix}
          </span>
        )}
      </div>

      <StepperButton onClick={() => onChange(clamp(value + 1))} label={`+1`}>
        <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" strokeLinecap="round">
          <path d="M7 3v8M3 7h8" />
        </svg>
      </StepperButton>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────

export function GenerationParams({
  bpm,
  measures,
  onBpmChange,
  onMeasuresChange,
}: GenerationParamsProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      {/* Header, clickable toggle */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 cursor-pointer group"
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          stroke="rgba(255,255,255,0.35)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-transform duration-150"
          style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
        >
          <path d="M3 1.5l4 3.5-4 3.5" />
        </svg>
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-text-muted group-hover:text-text-secondary transition-colors">
          Parametres
        </span>

        {/* Inline badges when collapsed */}
        {!expanded && (
          <div className="flex items-center gap-1.5 ml-2">
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
              style={{
                backgroundColor: "rgba(255,255,255,0.04)",
                color: "rgba(255,255,255,0.4)",
              }}
            >
              {bpm} BPM
            </span>
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
              style={{
                backgroundColor: "rgba(255,255,255,0.04)",
                color: "rgba(255,255,255,0.4)",
              }}
            >
              {measures} mesures
            </span>
          </div>
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="flex flex-col gap-2 pl-3">
          <Stepper
            label="Tempo"
            value={bpm}
            min={BPM_MIN}
            max={BPM_MAX}
            suffix="BPM"
            onChange={onBpmChange}
          />
          <Stepper
            label="Mesures"
            value={measures}
            min={MEASURES_MIN}
            max={MEASURES_MAX}
            onChange={onMeasuresChange}
          />
        </div>
      )}
    </div>
  );
}
